<template>
	<div class="currency-table-block" :class="{ mobile: isMobile }">
		<!-- Block Title with currency name, only exists when all currencies are being displayed -->
		<div class="block-title">
			<component :is="flagIcon"/>
			<div class="currency-name">{{currency.name}}</div>
		</div>
		<!-- Row for each product with accounts for this currency -->
		<component
		v-for="(product, index) in productsList"
        :is="tableRowComponent"
		:key="product.description"
        :index="index"
		:product="product"
		:currency="currency.name"
		:productsList="productsList"
		:accountFinancialType="accountFinancialType"
		:savedBackState="savedBackState"
        @mobileRowClick="emit('mobileRowClick', $event)"/>
		<!-- Total amount for this currency -->
		<div class="currency-total-amount-wrapper">
			<div class="label">{{ $t('accounts.patrimony.labels.total') }} {{ currency.name }}</div>
			<div class="amount">
                <!-- Icon -->
                <div class="icon" :class="{'negative': currencyTotalAmount < 0}"><component :is="amountIcon(currencyTotalAmount)"/></div>
				<!-- Amount -->
                <span>{{amountFormat(Math.abs(currencyTotalAmount), currency.name)}}</span>
            </div>
		</div>
	</div>
</template>

<script setup>
import DesktopProductTableRow from '@/components/features/accounts/patrimony/table/desktop/ProductTableRow'
import MobileProductTableRow from '@/components/features/accounts/patrimony/table/mobile/ProductTableRow'
/* Vue && Assets */
import { computed, defineAsyncComponent } from 'vue'
import { flagCurrenciesIconMapper } from '@/assets/scripts/mappers/flags'
import { calculateProductTotalAvailableAmountByCurrency, calculateProductTotalAmountByCurrency, calculateProductPercentage } from '@/assets/scripts/parsers/patrimony'
import { amountFormat } from '@/assets/scripts/amounts'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/** props received
@currency {string} - selected currency in our picker
@accountFinancialType {String} - type of financial family account
@savedBackState {Object} - info to make old state of table trigger when getting back from browser
@isMobile {boolean}
*/
const props = defineProps({
    currency: Object,
    accountFinancialType: String,
    savedBackState: Object,
    isMobile: Boolean
})

const emit = defineEmits(['mobileRowClick'])

const { getAccountsProductsList, getPatrimonyAccountsList } = mapGetters(moduleMapper.ACCOUNTS_PATRIMONY)
const { getAppThemeMode } = mapGetters()

const tableRowComponent = computed(() => {
    return !props.isMobile ? DesktopProductTableRow : MobileProductTableRow
})

const flagIcon = computed(() => {
    const icon = flagCurrenciesIconMapper(props.currency.name)
	return defineAsyncComponent(() => import('@/assets/icons/flags/' + icon + '.svg?inline'))
})

const productsList = computed(() => {
    const productsToDisplay = []
    let totalAmount = 0

    getAccountsProductsList.value(null, getAppThemeMode.value).forEach(product => {
        if (getPatrimonyAccountsList.value(props.currency.name, product.id, props.accountFinancialType).length) {
            const productInfo = {
                id: product.id,
                description: product.description,
                accounts: getPatrimonyAccountsList.value(props.currency.name, product.id, props.accountFinancialType)
            }

            productInfo.availableTotalAmount = calculateProductTotalAvailableAmountByCurrency(productInfo.accounts)
            productInfo.totalAmount = calculateProductTotalAmountByCurrency(productInfo.accounts)
            totalAmount += productInfo.totalAmount
            productsToDisplay.push(productInfo)
        }
    })

    return calculateProductPercentage(productsToDisplay, totalAmount)
})

const currencyTotalAmount = computed(() => {
    let currencyTotalAmount = 0
    productsList.value.forEach(product => {
        currencyTotalAmount += product.totalAmount
    })
    return currencyTotalAmount
})

const amountIcon = amount => {
	if (!amount || amount === 0) return null
	const icon = amount > 0 ? 'plus' : 'minus'
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
}
</script>

<style lang="scss" scoped>
.currency-table-block {
    width: 100%;
    box-sizing: border-box;

    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    padding: 16px;
    border-radius: 12px;

    background-color: var(--secondary-light-4);

    .block-title {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        gap: 8px;

        margin-bottom: 14px;

        svg {
            width: 16px;
            height: 16px;
        }

        .currency-name {
            @include H6-Medium;
            color: var(--secondary-dark-4);
        }
    }

    .currency-total-amount-wrapper {
        width: 100%;
        height: 40px;
        margin-top: 7px;
        padding: 16px 11px;
        border-radius: 8px;
        box-sizing: border-box;

        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;

        @include H6-Medium;
        color: var(--secondary-dark-2);

        background-color: var(--secondary-light-3);

        .amount {
            display: flex;
            text-align: right;

            .icon {
				margin-right: 2px;

                flex-shrink: 0;

                svg {
                    width: 12px;
                    height: 12px;

                    :deep(.change-color-fill) {
                        fill: var(--positive);
                    }
                }

				&.negative {
                    svg {
                        :deep(.change-color-fill) {
                            fill: var(--negative);
                        }
                    }
				}
			}
        }
    }

    &.mobile {
        padding: 16px 0;
        border-radius: unset;
        border-top-right-radius: 16px;
        border-top-left-radius: 16px;

        .block-title {
            padding: 0 16px;
            margin-bottom: 15px;
        }

        .currency-total-amount-wrapper {
            width: calc(100% - 32px);

            margin-top: 15px;
            padding: 0 16px;
            border-radius: 8px;

            .label {
                font-size: 15px;
                color: var(--primary-variant-4);
            }

            .amount {
                align-items: center;

                font-size: 14px;
                color: var(--secondary-variant-1);
            }
        }
    }
}
</style>
